import { lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import CandidatePrivateRoute from './CandidatePrivateRoute'
import CandidateRestrictedRoute from './CandidateRestrictedRoute'
import PrivateRoute from './PrivateRoute'
import RestrictedRoute from './RestrictedRoute'

// **SCSS Imports
import CandidateReport from '../pages/public-share/CandidateReport.jsx'
import './index.scss'

// **Auth Imports
const Login = lazy(() => import('../pages/auth/login'))
const Signup = lazy(() => import('../pages/auth/signup'))
const VerifyUser = lazy(() => import('../pages/auth/verify-user'))
const VerifyInvitedUser = lazy(() => import('../pages/auth/verify-invite-user'))
const ForgotPassword = lazy(() => import('../pages/auth/forgot-password'))
const ForgotPasswordVerify = lazy(() =>
  import('../pages/auth/forgot-password/verify/index')
)

// **Home Import
const HomeContainer = lazy(() => import('../pages/home/HomeContainer'))

// **Subscription Imports
const Subscription = lazy(() => import('../pages/subscription'))

// **Subscription Imports
const NoWorkspace = lazy(() => import('../pages/no-workspace'))

// **Dev user route imports
const TenantManager = lazy(() => import('../pages/tenant-manager/index'))
const Attempts = lazy(() => import('../pages/attempts/index.jsx'))
const BlacklistedDomain = lazy(() =>
  import('../pages/blacklisted-domains/index')
)
const TenantInvoices = lazy(() => import('../pages/invoices/index'))

// **Contest Import
const RegistrationForm = lazy(() => import('../pages/registration-form'))
const CreateRegistrationForm = lazy(() =>
  import('../pages/registration-form/create/index')
)
const RegistrationFormContainer = lazy(() =>
  import('../pages/registration-form/RegistrationFormContainer')
)
const ContestContainer = lazy(() => import('../pages/contest'))
const ContestList = lazy(() => import('../pages/contest/contest-list'))
const ContestCreate = lazy(() => import('../pages/contest/create/create'))
const EditContest = lazy(() => import('../pages/contest/edit'))
const StageDetails = lazy(() =>
  import('../pages/contest/edit/stage/stage-details')
)
const StagePreview = lazy(() =>
  import('../pages/contest/edit/stage/stage-preview')
)
const EvaluateAttempt = lazy(() =>
  import('../pages/contest/edit/stage/EvaluateAttempt')
)

const EvaluateMCQAttempt = lazy(() =>
  import('../pages/contest/edit/stage/EvaluateAttempt/EvaluateMCQAttempt')
)

const EvaluateAIScreening = lazy(() =>
  import('../pages/contest/edit/stage/EvaluateAttempt/ai-screening/index')
)

const SuspiciousActivity = lazy(() =>
  import('../pages/contest/edit/stage/suspicious-activity')
)
const CandidateSummary = lazy(() =>
  import('../pages/contest/edit/components/CandidateSummary')
)

// **Challenge Import
const ChallengeContainer = lazy(() => import('../pages/challenge'))
const ChallengeList = lazy(() => import('../pages/challenge/challenge-list'))
const EditChallenge = lazy(() => import('../pages/challenge/edit'))
const ChallengeEditor = lazy(() => import('../pages/challenge/editor'))

// ** Question bank Import
const Questions = lazy(() => import('../pages/questions'))

// **Settings Imports
const SettingsContainer = lazy(() => import('../pages/settings'))
const ProfileSettings = lazy(() => import('../pages/settings/profile'))
const UsersSettings = lazy(() => import('../pages/settings/users'))
const ThemeConfigSettings = lazy(() => import('../pages/settings/config'))
const WorkSpaceSettings = lazy(() => import('../pages/settings/workspace'))
const Integrations = lazy(() => import('../pages/settings/integrations'))

// **Candidate Details
const CandidateDetailsList = lazy(() => import('../pages/candidate-details'))
const CandidateDetailsEdit = lazy(() =>
  import('../pages/candidate-details/edit')
)

// Candidate Imports
const CandidateContainer = lazy(() => import('../pages/candidate'))
const ContestHome = lazy(() => import('../pages/candidate/contest'))
const CandidateRegistration = lazy(() =>
  import('../pages/candidate/registration')
)
const CandidateRegistrationDone = lazy(() =>
  import('../pages/candidate/registration/complete')
)
const ContestStage = lazy(() => import('../pages/candidate/contest-stage'))
const DemoChallenge = lazy(() =>
  import('../pages/candidate/contest-stage/programming-type/demo-challenge')
)
const CandidateHome = lazy(() => import('../pages/candidate/home'))

// Interview Stage
const InterviewStage = lazy(() => import('../pages/interview-stage'))

// Databases Imports
const DatabaseContainer = lazy(() =>
  import('../pages/databases/database-container')
)
const Databases = lazy(() => import('../pages/databases'))
const DatabaseDetailsContainer = lazy(() =>
  import('../pages/databases/database-details')
)

// Course Imports
const CourseContainer = lazy(() => import('../pages/courses'))

// Hiring Course Imports
const HiringCourses = lazy(() => import('../pages/courses/hiring'))
const HiringDetails = lazy(() => import('../pages/courses/hiring/edit/index'))
const TopicDetails = lazy(() =>
  import('../pages/courses/hiring/topic/edit/index')
)
const PageDetails = lazy(() =>
  import('../pages/courses/hiring/topic/page/edit/index')
)

// Learner Course Imports
const LearnerCourses = lazy(() => import('../pages/courses/learner'))
const LearnerCourseDetails = lazy(() =>
  import('../pages/courses/learner/details')
)
const LearnerCourseTopicPageDetails = lazy(() =>
  import('../pages/courses/learner/topic/page')
)

// Evaluation Template
const EvaluationTemplateContainer = lazy(() => import('../pages/template'))
const EvaluationTemplateList = lazy(() => import('../pages/template/list'))
const CreateEvaluationTemplate = lazy(() => import('../pages/template/create'))

// Email template
const EmailTemplateContainer = lazy(() =>
  import('../pages/email-template/EmailTemplateContainer')
)
const EmailTemplateList = lazy(() => import('../pages/email-template/list'))
const EditEmailTemplate = lazy(() =>
  import('../pages/email-template/edit/index')
)

// AI Interview related components
const AIScreeningStage = lazy(() => import('../pages/ai-screening-stage'))

// Video Response State
const VideoResponseStageType = lazy(() =>
  import('../pages/candidate/contest-stage/video-response-stage')
)

const VideoResponseEvaluation = lazy(() =>
  import(
    '../pages/contest/edit/stage/EvaluateAttempt/video-response-evaluate/index'
  )
)

// Tenant approvals
const UserApprovals = lazy(() => import('../pages/settings/user-approvals'))

// Public Share Container
const PublicShareContainer = lazy(() =>
  import('../pages/public-share/index.jsx')
)

const RouterComponent = () => {
  return (
    <Routes>
      <Route path="/login" element={<RestrictedRoute />}>
        <Route index element={<Login />} />
      </Route>
      <Route path="/signup" element={<RestrictedRoute />}>
        <Route index element={<Signup />} />
      </Route>

      <Route path="/verify-user/:hash" element={<RestrictedRoute />}>
        <Route index element={<VerifyUser />} />
      </Route>

      <Route
        path="/verify-invited-user/:hash"
        element={<VerifyInvitedUser />}
      />

      <Route path="/forgot-password" element={<RestrictedRoute />}>
        <Route index element={<ForgotPassword />} />
      </Route>
      <Route path="/forgot-password/verify" element={<RestrictedRoute />}>
        <Route index element={<ForgotPasswordVerify />} />
      </Route>

      <Route path="/" element={<PrivateRoute />}>
        <Route index element={<HomeContainer />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="no-workspace" element={<NoWorkspace />} />
        <Route path="question-bank-management" element={<Questions />} />
        <Route path="tenant-manager" element={<TenantManager />} />
        <Route path="attempts" element={<Attempts />} />
        <Route path="tenant-invoices" element={<TenantInvoices />} />
        <Route path="blacklisted-domain" element={<BlacklistedDomain />} />
        <Route path="application-form" element={<RegistrationFormContainer />}>
          <Route index element={<RegistrationForm />} />
          <Route path="create" element={<CreateRegistrationForm />} />
          <Route path="edit/:formId" element={<CreateRegistrationForm />} />
        </Route>
        <Route path="databases" element={<DatabaseContainer />}>
          <Route index element={<Databases />} />
          <Route path=":id/edit" element={<DatabaseDetailsContainer />} />
          <Route path="*" element={<Navigate to={''} replace />} />
        </Route>

        <Route
          path="evaluation-template"
          element={<EvaluationTemplateContainer />}
        >
          <Route index element={<EvaluationTemplateList />} />
          <Route path="create" element={<CreateEvaluationTemplate />} />
          <Route path="edit/:id" element={<CreateEvaluationTemplate />} />
          <Route path="*" element={<Navigate replace to={''} />} />
        </Route>
        <Route path="email-template" element={<EmailTemplateContainer />}>
          <Route index element={<EmailTemplateList />} />
          <Route path="edit/:templateId" element={<EditEmailTemplate />} />
          <Route path="*" element={<Navigate replace to={''} />} />
        </Route>

        <Route path="candidate-details" element={<CandidateDetailsList />} />
        <Route
          path="candidate-details/edit/:candidateId"
          element={<CandidateDetailsEdit />}
        />
        <Route path="courses" element={<CourseContainer />}>
          <Route index element={<HiringCourses />} />
          <Route path=":courseId/edit" element={<HiringDetails />} />
          <Route
            path=":courseId/topics/:topicId/edit"
            element={<TopicDetails />}
          />
          <Route
            path=":courseId/topics/:topicIds/pages/:pageId/edit"
            element={<PageDetails />}
          />
          <Route path="*" element={<Navigate to={''} replace />} />
        </Route>

        <Route path="learning/courses" element={<CourseContainer />}>
          <Route index element={<LearnerCourses />} />
          <Route path=":id" element={<LearnerCourseDetails />} />
          <Route
            path=":id/topics/:topicId/pages/:pageId"
            element={<LearnerCourseTopicPageDetails />}
          />
          <Route path="*" element={<Navigate to={''} replace />} />
        </Route>

        <Route path="contests" element={<ContestContainer key="contest" />}>
          <Route index element={<ContestList />} />
          <Route path="create" element={<ContestCreate />} />
          <Route
            path=":contestId/ppt-stage/:stageId"
            element={<InterviewStage ppt={true} />}
          />
          <Route
            path=":contestId/interview-stage/:stageId/candidate/:candidateId"
            element={<InterviewStage />}
          />
          <Route path=":contestId/edit" element={<EditContest />} />

          <Route
            path=":contestId/edit/stage/:stageId"
            element={<StageDetails />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/mcq-evaluate/:attemptId"
            element={<EvaluateMCQAttempt />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/video-response-evaluate/:attemptId"
            element={<VideoResponseEvaluation />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId"
            element={<EvaluateAIScreening />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/evaluate/:attemptId"
            element={<EvaluateAttempt />}
          />

          <Route
            path=":contestId/edit/stage/:stageId/suspicious-activity/:attemptId"
            element={<SuspiciousActivity />}
          />
          <Route
            path=":contestId/edit/stage/:stagePreviewId/preview"
            element={<StagePreview />}
          />
          <Route path="*" element={<Navigate to={''} replace />} />
        </Route>
        <Route
          path="assessments"
          element={<ContestContainer key="assessment" />}
        >
          <Route index element={<ContestList assessment={true} />} />
          <Route path="create" element={<ContestCreate assessment={true} />} />
          <Route
            path=":contestId/ppt-stage/:stageId"
            element={<InterviewStage ppt={true} assessment={true} />}
          />
          <Route
            path=":contestId/interview-stage/:stageId/candidate/:candidateId"
            element={<InterviewStage assessment={true} />}
          />
          <Route
            path=":contestId/edit"
            element={<EditContest assessment={true} />}
          />
          <Route
            path=":contestId/candidate-summary/:candidateId"
            element={<CandidateSummary />}
          />
          <Route
            path=":contestId/edit/stage/:stageId"
            element={<StageDetails assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/mcq-evaluate/:attemptId"
            element={<EvaluateMCQAttempt assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/video-response-evaluate/:attemptId"
            element={<VideoResponseEvaluation assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId"
            element={<EvaluateAIScreening assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/evaluate/:attemptId"
            element={<EvaluateAttempt assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/suspicious-activity/:attemptId"
            element={<SuspiciousActivity assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stagePreviewId/preview"
            element={<StagePreview assessment={true} />}
          />
          <Route path="*" element={<Navigate to={''} replace />} />
        </Route>
        <Route path="challenges" element={<ChallengeContainer />}>
          <Route index element={<ChallengeList />} />
          <Route path="editor" element={<ChallengeEditor />} />
          <Route path=":challengeId/edit" element={<EditChallenge />} />
          <Route path="*" element={<Navigate to={''} replace />} />
        </Route>

        <Route path="settings" element={<SettingsContainer />}>
          <Route index element={<Navigate to="users" replace />} />

          <Route path={'users'} element={<UsersSettings />} />
          <Route path={'workspaces'} element={<WorkSpaceSettings />} />
          <Route path={'integrations'} element={<Integrations />} />
          <Route path={'config'} element={<ThemeConfigSettings />} />
          <Route path={'profile'} element={<ProfileSettings />} />
          <Route path={'user-approvals'} element={<UserApprovals />} />
          <Route path="*" element={<Navigate to={'users'} replace />} />
        </Route>
        <Route path="*" element={<Navigate to={'/'} replace />} />
      </Route>

      {/* Global candidate restricted routes */}
      <Route path="/candidates" element={<CandidateContainer />}>
        <Route index element={<Navigate to="login" replace />} />
        <Route path="login" element={<CandidateRestrictedRoute />}>
          <Route index element={<Login globalCandidatePath={true} />} />
        </Route>
        <Route path="signup" element={<CandidateRestrictedRoute />}>
          <Route index element={<Signup globalCandidatePath={true} />} />
        </Route>
        <Route path="verify" element={<CandidateRestrictedRoute />}>
          <Route index element={<VerifyUser globalCandidatePath={true} />} />
        </Route>
        <Route path="forgot-password" element={<CandidateRestrictedRoute />}>
          <Route
            index
            element={<ForgotPassword globalCandidatePath={true} />}
          />
        </Route>
        <Route
          path="forgot-password/verify"
          element={<CandidateRestrictedRoute />}
        >
          <Route
            index
            element={<ForgotPasswordVerify globalCandidatePath={true} />}
          />
        </Route>
        <Route path="*" element={<Navigate to="login" replace />} />
      </Route>

      {/* Candidate  routes  */}
      <Route path="/c" element={<CandidateContainer />}>
        <Route path="" element={<CandidatePrivateRoute />}>
          <Route index element={<CandidateHome />} />
        </Route>
        <Route path="profile" element={<CandidatePrivateRoute />}>
          <Route index element={<ProfileSettings />} />
        </Route>

        {/* Candidates Contest Routes */}
        <Route path=":contestUri" element={<CandidateContainer />}>
          <Route index element={<Navigate to="stages" replace />} />
          <Route path="stages" element={<CandidatePrivateRoute />}>
            <Route index element={<ContestHome />} />
          </Route>
          <Route path="login" element={<CandidateRestrictedRoute />}>
            <Route index element={<Login candidatePath={true} />} />
          </Route>
          <Route path="signup" element={<CandidateRestrictedRoute />}>
            <Route index element={<Signup candidatePath={true} />} />
          </Route>
          <Route path="verify" element={<CandidateRestrictedRoute />}>
            <Route index element={<VerifyUser candidatePath={true} />} />
          </Route>
          <Route path="forgot-password" element={<CandidateRestrictedRoute />}>
            <Route index element={<ForgotPassword candidatePath={true} />} />
          </Route>
          <Route path="registration" element={<CandidateRegistration />} />
          <Route
            path="registration/success"
            element={<CandidateRegistrationDone />}
          />

          <Route
            path="forgot-password/verify"
            element={<CandidateRestrictedRoute />}
          >
            <Route
              index
              element={<ForgotPasswordVerify candidatePath={true} />}
            />
          </Route>

          <Route
            path="verify-invited"
            element={<VerifyInvitedUser candidatePath={true} />}
          />

          <Route
            path="demo-challenge/:demoChallengeId"
            element={<CandidatePrivateRoute />}
          >
            <Route index element={<DemoChallenge />} />
          </Route>

          <Route path=":stageId" element={<CandidatePrivateRoute />}>
            <Route index element={<ContestStage />} />
          </Route>

          <Route
            path=":contestId/ppt-stage/:stageId"
            element={<CandidatePrivateRoute />}
          >
            <Route index element={<InterviewStage ppt={true} />} />
          </Route>

          <Route
            path=":contestId/interview-stage/:stageId"
            element={<CandidatePrivateRoute />}
          >
            <Route index element={<InterviewStage />} />
          </Route>

          <Route
            path=":contestId/ai-screening-stage/:stageId"
            element={<CandidatePrivateRoute />}
          >
            <Route index element={<AIScreeningStage />} />
          </Route>
          <Route
            path=":contestId/video-response-stage/:stageId"
            element={<CandidatePrivateRoute />}
          >
            <Route index element={<VideoResponseStageType />} />
          </Route>
        </Route>
      </Route>

      {/* Dev user routes */}
      <Route path="/dev/login" element={<RestrictedRoute />}>
        <Route index element={<Login devUser={true} />} />
      </Route>

      <Route path="/dev/forgot-password" element={<RestrictedRoute />}>
        <Route index element={<ForgotPassword developerPath={true} />} />
      </Route>
      <Route path="/dev/forgot-password/verify" element={<RestrictedRoute />}>
        <Route index element={<ForgotPasswordVerify developerPath={true} />} />
      </Route>

      {/* Public routes */}
      <Route path="/share" element={<PublicShareContainer />}>
        <Route path=":publicId" element={<CandidateReport />} />
        <Route
          path=":publicId/assessments"
          element={<ContestContainer key="public" />}
        >
          <Route
            path=":contestId/edit/stage/:stageId/mcq-evaluate/:attemptId"
            element={<EvaluateMCQAttempt assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/video-response-evaluate/:attemptId"
            element={<VideoResponseEvaluation assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId"
            element={<EvaluateAIScreening assessment={true} />}
          />
          <Route
            path=":contestId/edit/stage/:stageId/evaluate/:attemptId"
            element={<EvaluateAttempt assessment={true} />}
          />
          <Route
            path=":contestId/candidate-summary/:candidateId"
            element={<CandidateSummary />}
          />
          <Route path="*" element={<Navigate to={'/login'} replace />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default RouterComponent
